import { computed, inject } from '@angular/core';
import {
  CoreNotificationType,
  InAppNotification,
  autoGeneratedNotificationTypes,
} from '@kinet-open-network/shared/data-access/model';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { NotificationsService } from '../services/notifications.service';

export interface NotificationsStateSlice {
  notifications: InAppNotification[];
}

export function withNotifications() {
  return signalStoreFeature(
    {
      state: type<{ unreadNotificationsCount: number }>(),
    },
    withState<NotificationsStateSlice>({
      notifications: [],
    }),
    withComputed((store) => ({
      unread: computed(() =>
        store
          .notifications()
          .filter(
            (n) =>
              !n.read &&
              !autoGeneratedNotificationTypes.includes(
                n.type || CoreNotificationType.TaskAssigned,
              ),
          ),
      ),
      autoGeneratedNotifications: computed(() =>
        store
          .notifications()
          .filter((n) =>
            autoGeneratedNotificationTypes.includes(
              n.type || CoreNotificationType.TaskAssigned,
            ),
          ),
      ),
      read: computed(() => store.notifications().filter((n) => !!n.read)),
    })),
    withMethods(
      (store, notificationsService = inject(NotificationsService)) => ({
        loadNotifications: async () => {
          try {
            const notifications = await firstValueFrom(
              notificationsService.getNotifications(),
            );
            patchState(store, { notifications });
          } catch (error) {
            patchState(store, { notifications: [] });
          }
        },
        loadUnreadNotificationsCount: async () => {
          try {
            const unreadNotificationsCount = await firstValueFrom(
              notificationsService.getNotificationsUnreadCount(),
            );
            patchState(store, { unreadNotificationsCount });
          } catch (error) {
            // Handle error if needed
          }
        },
        markNotificationAsRead: async (notification: InAppNotification) => {
          try {
            await firstValueFrom(
              notificationsService.markNotificationAsRead(notification),
            );
            patchState(store, {
              notifications: store
                .notifications()
                .map((n) =>
                  n.id === notification.id ? { ...n, read: true } : n,
                ),
              unreadNotificationsCount: Math.max(
                0,
                store.unreadNotificationsCount() - 1,
              ),
            });
          } catch (error) {
            // Handle error if needed
          }
        },
      }),
    ),
  );
}
